/**
 * The session seam (#26 / R6.1).
 *
 * The shell had no way to learn who is acting: `ActorContext` held `null` and said so. This module
 * is the one place that asks the server, and it is deliberately thin — it owns no authorization.
 * The server decides what an actor may do on every request, and #28 (R6.3) proves that with
 * positive and negative cases.
 *
 * Four measured facts about the deployed backend shape this file. Each one is a defect waiting to
 * happen if it is assumed instead of read:
 *
 * 1. **Session-only auth.** `server/auth/middleware/auth.js` accepts a session cookie and nothing
 *    else since #348. There is no bearer token to hold, so there is nothing to store in this app.
 * 2. **`GET /api/auth/verify` is the session endpoint.** There is no `/api/auth/me`. `verify`
 *    returns `{ valid: true, user: {...} }` for a live session and 401 for none.
 * 3. **The FIRST login needs a CSRF cookie that a first-time visitor does not have.**
 *    `app/src/api/client.js` refuses every mutation without `chorestory.csrf`, which is stricter
 *    than the server: `csrfProtect` only enforces the double-submit check once a session cookie
 *    exists. So sign-in mints the token first, through the endpoint built for it. Keeping the
 *    client guard universal is the point — an exemption for `/api/auth/login` would be a hole that
 *    grows.
 * 4. **The login body field is `username`, and it accepts an email.**
 *    `authService.login(identifier)` branches on `identifier.includes('@')`. The recovered Login
 *    surface collects an email, so the mapping happens here, once, in the open.
 *
 * What this module deliberately does NOT do: invent a `child`, `device` or `operator` authority. A
 * tenant session carries `role: 'admin' | 'user'` and the backend has no child role at all. Child
 * profiles and kiosk devices arrive through the PIN and display-token flows, which #32 (R8.1) owns.
 */

import { ApiError, api } from './client.js';
import { isFamilyGenerationRetired } from '../lib/familyTurnFence.js';

export const SESSION_PATH = '/api/auth/verify';
export const LOGIN_PATH = '/api/auth/login';
export const LOGOUT_PATH = '/api/auth/logout';
export const CSRF_PATH = '/api/auth/csrf-token';
export const SIGNUP_PATH = '/api/auth/signup/family';
export const SLUG_CHECK_PATH = '/api/auth/signup/check-slug';
export const RESEND_VERIFICATION_PATH = '/api/auth/signup/resend-verification';
export const VERIFY_EMAIL_PATH = '/api/auth/signup/verify-email';
export const TENANT_PATH = '/api/tenants/current';
export const MEMBERS_PATH = '/api/users';
export const PROFILES_PATH = '/api/profiles';
export const ACTOR_PATH = '/api/profiles/actor';
export const REAUTH_PATH = '/api/auth/reauth';
export const FORGOT_PASSWORD_PATH = '/api/auth/forgot-password';
export const RESET_PASSWORD_PATH = '/api/auth/reset-password';
export const VERIFY_RESET_TOKEN_PATH = '/api/auth/verify-reset-token';
export const INVITATIONS_PATH = '/api/auth/signup/invitations';

const CSRF_COOKIE = 'chorestory.csrf';

/**
 * Map a `/api/auth/verify` payload to an actor, or to `null` when the payload does not describe a
 * usable signed-in person.
 *
 * "Usable" is checked, not assumed (review M3). An actor is what every surface scopes its queries
 * by, so a half-built one — `tenantId: undefined` — is worse than none at all: it turns a missing
 * field into a query against nothing rather than into an error anyone can see. `id` is normalised
 * to a string (review L3), because a numeric `users.id` otherwise flows unchanged into the theme
 * profile key and the feedback identity string.
 *
 * `null` here means only "this payload names nobody". It does NOT mean "signed out" — the CALLER
 * decides that, because on `/api/auth/verify` a 200 that names nobody is a fault, not an answer.
 *
 * @param {{valid?: boolean, user?: object}|null|undefined} payload
 * @returns {{id: string, kind: 'parent', username: string, role: string, isAdmin: boolean,
 *            tenantId: string, tenantSlug: string, authProvider: string}|null}
 */
export function toActor(payload) {
  const user = payload && payload.valid !== false ? payload.user : null;
  if (!user || typeof user !== 'object' || Array.isArray(user)) return null;
  // #582: a shared display is never a person. Before slice 6 its session borrows the household's
  // first admin, so without this line a kiosk would be handed that parent's screens.
  if (isDeviceUser(user)) return null;

  const rawId = user.id ?? user.userId;
  const id = rawId === null || rawId === undefined ? '' : String(rawId);
  // Every field below is read by a surface. A missing one is a defect to surface, not to paper over.
  if (!id || !user.tenantId || !user.username) return null;

  const role = user.role || 'user';
  return {
    id,
    // A tenant session is a grown-up's session. The backend has no child role, so nothing here
    // may claim one.
    kind: 'parent',
    username: user.username,
    role,
    isAdmin: role === 'admin',
    tenantId: user.tenantId,
    tenantSlug: user.tenantSlug,
    authProvider: user.authProvider,
    // The current verify endpoint omits this. Preserve an explicit report, never infer one.
    ...(typeof user.emailVerified === 'boolean' ? { emailVerified: user.emailVerified } : {}),
  };
}

/**
 * Does this `/api/auth/verify` user describe a shared DISPLAY rather than a person (#582)?
 *
 * Three markers, any one of which is enough: the v2 device contract (`kind: 'device'`), and the
 * two a borrowed-admin (v1) display session carries today (`authProvider: 'display_token'`, and
 * `isDisplayToken` where a server sends it).
 */
function isDeviceUser(user) {
  return user.kind === 'device' || user.authProvider === 'display_token' || user.isDisplayToken === true;
}

/**
 * Map a `/api/auth/verify` payload to a shared display, or `null` when it names a person or nobody.
 *
 * `contractVersion` is what a screen reads before it enables anything: only `2` means the server
 * answers this display as a device with its own board and PIN-proved actor (slice 6 switches the
 * exchange over). A v1 display is still a device — it is simply not on that contract yet, so the
 * live board must say it is not available rather than borrow the parent's view.
 *
 * @returns {{kind:'device', contractVersion: 2|1|null, tenantId: string|null, tenantSlug: string|null,
 *            displayTokenId: *, name: string|null}|null}
 */
export function toDevice(payload) {
  const user = payload && payload.valid !== false ? payload.user : null;
  if (!user || typeof user !== 'object' || Array.isArray(user) || !isDeviceUser(user)) return null;
  const v2 = user.kind === 'device' && user.contractVersion === 2;
  return {
    kind: 'device',
    contractVersion: v2 ? 2 : user.kind === 'device' ? null : 1,
    tenantId: user.tenantId ?? null,
    tenantSlug: user.tenantSlug ?? null,
    displayTokenId: user.displayTokenId ?? null,
    name: typeof user.displayTokenName === 'string' && user.displayTokenName.trim() ? user.displayTokenName.trim() : null,
  };
}

/**
 * What a display screen needs to know about this browser: a display, a person, or nobody.
 *
 * @returns {Promise<{status:'device', device:object}|{status:'person'}|{status:'none'}>}
 * @throws {ApiError} for anything that is not one of those three clean answers.
 */
export async function fetchDisplaySession() {
  let payload;
  try {
    payload = await api.get(SESSION_PATH, { scope: 'device' });
  } catch (error) {
    // Every 401 here ends in the same place — pair the display again — whether the token was
    // revoked, the session expired or the household was suspended. The screen says which it can.
    if (error instanceof ApiError && error.status === 401) return { status: 'none' };
    throw error;
  }
  const device = toDevice(payload);
  if (device) return { status: 'device', device };
  if (toActor(payload)) return { status: 'person' };
  throw new ApiError('the session endpoint answered 200 without naming a display or a person', {
    status: 200,
    path: SESSION_PATH,
    body: payload,
  });
}

/**
 * True only when the browser holds a csrf cookie with a REAL value.
 *
 * The emptiness check is the whole point (review M1). `client.js` rejects an empty token, so a
 * presence-only check here would let `ensureCsrfToken` decide there was nothing to do and leave
 * the mutation to fail on a retry that could never succeed.
 */
function hasCsrfCookie() {
  return document.cookie
    .split('; ')
    .some((part) => part.startsWith(`${CSRF_COOKIE}=`) && part.length > CSRF_COOKIE.length + 1);
}

/**
 * Make sure the browser holds a CSRF token before a mutation is attempted.
 *
 * Called only on the sign-in path. Every later mutation runs with the cookie the server set
 * alongside the session, so this costs one request on a cold browser and none after that.
 */
export async function ensureCsrfToken() {
  if (hasCsrfCookie()) return;
  await api.get(CSRF_PATH);
}

/**
 * Ask the server who is acting.
 *
 * @returns {Promise<object|null>} the actor, or `null` when nobody is signed in. While the server
 *   describes a family turn (#580 D1), or refuses for one, `{ actor, familyTurn }` instead: the
 *   member or nobody, and the turn's state (`toFamilyTurn`). Never the account.
 * @throws {ApiError} for anything that is not a clean answer. Two cases are deliberate:
 *
 *   - **A 200 that does not describe a user is a FAULT, not a signed-out family** (review H1).
 *     `client.js` hands back `{raw: text}` for a non-JSON 200, which is exactly what the SPA's own
 *     `index.html` produced before #472 fixed the API route. Reporting that as "nobody is signed
 *     in" shows the front door to a family that is logged in, and loses their place.
 *   - **A tenant-lock 401 is its own answer** (review M2). `server/auth/middleware/auth.js` returns
 *     401 `{error: 'Tenant locked'}` for a suspended family. Folding it into "please log in" makes
 *     them sign in and be signed straight back out with nothing explaining why.
 */
export async function fetchSession() {
  const requestedAt = childClockStamp();
  let payload;
  try {
    payload = await api.get(SESSION_PATH);
  } catch (error) {
    // #580 D1: a revoked session and an unreadable turn store are ANSWERS about the turn, never a
    // plain sign-out and never the account.
    const familyTurn = familyTurnRefusal(error);
    if (familyTurn) return { actor: null, familyTurn };
    if (isSignedOut(error)) return null;
    throw error;
  }
  const actor = accountFrom(payload);
  if (!actor) return null;

  // #580 D1 (#898): once the server describes a family turn, the answer is the TURN's: the member
  // while it is active, nobody otherwise. The account is never returned, and the acting-profile
  // fallback below never runs. `{ actor, familyTurn }` is the shape `ActorContext` reads.
  if (payload.familyTurn !== undefined) return familyTurnAnswer(toFamilyTurn(payload.familyTurn), actor, requestedAt);

  // #578 gap G2 — a child acting on this device survives a refresh. The server holds the acting
  // profile and reads an expired one as nobody. If that read FAILS, the account is returned: the
  // parent's screens are readable by the owner's decision, and every parent change still needs the
  // parent window on the server. The marker keeps own-person notification preferences hidden.
  //
  // ONLY on this boot path. The three credential paths (`signIn`, `signUp`, `acceptInvitation`)
  // call `fetchAccountSession` directly: each regenerates the session, so nobody can be acting
  // after one, and each budgets exactly one confirming GET.
  try {
    const child = toChildActor(await fetchActingProfile(), actor);
    if (child) return child;
  } catch {
    // Account screens remain readable, but own-person preferences must not guess who is acting.
    return { ...actor, actingUnknown: true };
  }
  return actor;
}

/**
 * The signed-in ACCOUNT only — `fetchSession` without the acting profile. Same answers and the
 * same errors as documented above `fetchSession`.
 */
async function fetchAccountSession() {
  let payload;
  try {
    payload = await api.get(SESSION_PATH);
  } catch (error) {
    if (isSignedOut(error)) return null;
    throw error;
  }
  return accountFrom(payload);
}

/** A plain 401: nobody is signed in. The suspended-tenant 401 is not one. */
function isSignedOut(error) {
  return error instanceof ApiError && error.status === 401 && !isTenantLocked(error.body);
}

/** The account a verify payload names, `null` for a display, and a throw for anything unusable. */
function accountFrom(payload) {
  // A shared display is not an account (#582). Reporting nobody keeps a kiosk out of the
  // parent's screens; its own screen is `#/display`, which reads `fetchDisplaySession`.
  if (toDevice(payload)) return null;

  const actor = toActor(payload);
  if (!actor) {
    throw new ApiError('the session endpoint answered 200 without naming a usable account', {
      status: 200,
      path: SESSION_PATH,
      body: payload,
    });
  }

  return actor;
}

/** The suspended-tenant 401, which must not read as an ordinary signed-out state. */
function isTenantLocked(body) {
  return typeof body?.error === 'string' && body.error.toLowerCase().includes('tenant locked');
}

/** The descriptor version this app reads (`server/auth/familyTurn.js` `DESCRIPTOR_VERSION`). */
const FAMILY_TURN_VERSION = 2;
const LOCKED_TURN = Object.freeze({ state: 'locked' });
const REVOKED_TURN = Object.freeze({ state: 'revoked' });
const UNREADABLE_TURN = Object.freeze({ state: 'unreadable' });

/**
 * #580 D1 — read a family-turn descriptor (`familyTurn.describeTurn`) into what a screen may show:
 * `active` (one member, one generation), `locked`, `revoked` or `unreadable`. Presentation input
 * only: the server enforces the turn on every request.
 *
 * Fails closed. An unknown version or mode, or an active turn without a generation or a member, is
 * `unreadable`, and nobody is shown as acting.
 */
export function toFamilyTurn(descriptor) {
  if (!descriptor || typeof descriptor !== 'object' || descriptor.version !== FAMILY_TURN_VERSION) return UNREADABLE_TURN;
  if (descriptor.mode === 'locked') return LOCKED_TURN;
  if (descriptor.mode === 'revoked') return REVOKED_TURN;
  const { generation, member } = descriptor;
  if (descriptor.mode !== 'active' || typeof generation !== 'string' || !generation
      || !member || typeof member !== 'object' || member.userId == null) return UNREADABLE_TURN;
  return {
    state: 'active',
    generation,
    member: { userId: member.userId, firstName: member.firstName ?? null },
    expiresAt: descriptor.expiresAt ?? null,
    serverNow: descriptor.serverNow ?? null,
  };
}

/** The two refusals `/api/auth/verify` gives for a turn (`familyTurn.js`), as turn states. */
function familyTurnRefusal(error) {
  if (!(error instanceof ApiError)) return null;
  if (error.status === 401 && error.body?.code === 'FAMILY_TURN_REVOKED') return REVOKED_TURN;
  if (error.status === 503 && error.body?.code === 'FAMILY_TURN_UNAVAILABLE') return UNREADABLE_TURN;
  return null;
}

/**
 * The session answer while a turn exists: the member as a child actor when it is active, nobody
 * otherwise. `household` names the family only (a locked picker still builds its child in it), never
 * the account's person, role or name.
 */
function familyTurnAnswer(turn, account, requestedAt) {
  const household = { tenantId: account.tenantId, tenantSlug: account.tenantSlug };
  // #903: a generation this tab returned to the picker stays there, though the server may still hold it (a
  // renewal it saved whose answer was lost). A reload is not a PIN.
  if (turn.state === 'active' && isFamilyGenerationRetired(turn.generation)) return { actor: null, familyTurn: { state: 'locked', household } };
  const familyTurn = { ...turn, household };
  if (turn.state !== 'active') return { actor: null, familyTurn };
  const acting = { userId: turn.member.userId, firstName: turn.member.firstName, generation: turn.generation,
    expiresAt: turn.expiresAt, serverNow: turn.serverNow };
  return { actor: toChildActor(acting, account, requestedAt), familyTurn };
}

/**
 * Sign in with an email (or username) and a password.
 *
 * The login response is NOT trusted to mean a session exists (review H3).
 * `server/auth/routes/auth.routes.js` wraps session creation in its own `try/catch`, logs a
 * failure, and still answers 200 with the user — and it skips the block entirely when
 * `req.session` is absent. So a 200 here proves the PASSWORD was right and nothing else. The
 * server is asked who is acting, and that answer is what gets published. One extra GET on the
 * sign-in path buys the difference between "signed in" and "the server agrees you are signed in".
 *
 * @param {{identifier: string, password: string, rememberMe?: boolean}} credentials
 * @returns {Promise<object>} the signed-in actor, as the SERVER reports it.
 * @throws {ApiError} 401 for a rejected credential, 403 for an unverified admin email, and a
 *   distinct error when the credential was accepted but no session stuck.
 */
export async function signIn({ identifier, password, rememberMe = false } = {}) {
  const username = typeof identifier === 'string' ? identifier.trim() : '';
  if (!username || !password) {
    throw new ApiError('an email and a password are both required', {
      status: 0,
      path: LOGIN_PATH,
      body: null,
    });
  }

  await ensureCsrfToken();
  await api.post(LOGIN_PATH, { username, password, rememberMe });

  const actor = await fetchAccountSession();
  if (!actor) {
    throw new ApiError(
      'your password was accepted, but the sign-in did not stick — please try again',
      { status: 0, path: SESSION_PATH, body: null },
    );
  }
  return actor;
}

/**
 * Create a household, then ask the server who is acting.
 *
 * This is `signIn`'s shape, for the same measured reason and one worse case.
 * `server/auth/routes/signup.routes.js` wraps session creation in its own `try/catch`, logs a
 * failure, and still answers **201**. So a 201 proves the HOUSEHOLD EXISTS and nothing else.
 *
 * The difference from sign-in matters to the family. A failed sign-in can simply be retried; a
 * signup whose session did not stick has already created their household, and telling them
 * "signup failed" sends them to create it a second time under a slug that is now taken. So the
 * thrown error carries `householdCreated: true`, and the caller must say so.
 *
 * The published actor is checked against the account that was just created (Astra, 2026-09-08). A
 * session naming a different tenant is worse than no session at all: every later query would be
 * scoped to somebody else's family.
 *
 * @param {{familyName: string, slug: string, displayName: string, email: string,
 *          password: string, tosAccepted: boolean}} form
 * @returns {Promise<{actor: object, tenant: object, verificationEmailSent: boolean,
 *          emailVerified: boolean}>}
 * @throws {ApiError} with `householdCreated` set when the family exists but the session does not.
 */
export async function signUp({
  familyName,
  slug,
  displayName,
  email,
  password,
  tosAccepted,
  // The Settings stop (F-02). BOTH ARE OPTIONAL, and they are deliberately kept out of `body`
  // below: every key in `body` is required by the pre-flight, and the server itself falls back to
  // America/Edmonton and USD when a signup names neither
  // (`server/services/signup.service.js:188-189`). Sending an empty string would be harmless —
  // the service treats it as absent — but sending nothing is the honest shape.
  timezone,
  currency,
  // A Google signup carries NO password, and this flag is what stops the client demanding one.
  //
  // `server/auth/routes/signup.routes.js:33` reads the Google-verified profile from the SESSION;
  // when it carries a `googleId` the server takes the Google branch and never looks at a password
  // (`:34-35` is the local branch that does). The pre-flight below rejected an empty password
  // before the network, so a first-time Google user could not submit at all — the form was not
  // merely awkward, it was impassable (#543 fault A).
  fromGoogle = false,
} = {}) {
  const clean = (value) => (typeof value === 'string' ? value.trim() : '');
  const body = {
    familyName: clean(familyName),
    slug: clean(slug),
    displayName: clean(displayName),
    adminEmail: clean(email),
    password: typeof password === 'string' ? password : '',
  };

  // Fail before the network, so a missing field never costs a rate-limit slot: the signup limiter
  // allows 10 attempts per 15 minutes (`server/index.js:139`).
  const missing = Object.entries(body)
    .filter(([key, value]) => {
      // The password is the server's business, not this client's, once Google has vouched for the
      // person. Everything else is still required in both flows.
      if (key === 'password' && fromGoogle) return false;
      return !value;
    })
    .map(([key]) => key);
  if (missing.length) {
    throw new ApiError(`these are still needed: ${missing.join(', ')}`, {
      status: 0,
      path: SIGNUP_PATH,
      body: null,
    });
  }
  // The server's schema demands the literal `true`, and this is a consent decision — never a
  // default this client supplies on the family's behalf.
  if (tosAccepted !== true) {
    throw new ApiError('the Terms of Service and Privacy Policy have to be accepted first', {
      status: 0,
      path: SIGNUP_PATH,
      body: null,
    });
  }

  await ensureCsrfToken();

  // A failure here is NOT automatically "nothing was created". The first version of this comment
  // said it was, and the server contradicts it.
  //
  // `signup.service.js` commits the tenant, the admin user and the consent row in one
  // transaction, and THEN does un-guarded post-commit work — `await ensureTenantDirs(slug)`, a
  // bare recursive `mkdir`. `recursive: true` swallows EEXIST and nothing else, so EACCES,
  // EROFS or ENOSPC throws, reaches the route's outer catch, and answers 500 with the household
  // already committed and no session ever minted. A dropped connection or a proxy 502 lands the
  // family in the same place.
  //
  // So the split is by what the status actually PROVES:
  //   4xx  the server judged the submission and refused it. Nothing was created.
  //   5xx / network  nobody knows. The household may exist, and the family must be told that
  //                  rather than invited to create a second one.
  //
  // Found by qwen3.8-max reviewing the frozen candidate 44d9d5c.
  let created;
  try {
    created = await api.post(SIGNUP_PATH, {
      ...body,
      ...(clean(timezone) ? { timezone: clean(timezone) } : {}),
      ...(clean(currency) ? { currency: clean(currency) } : {}),
      tosAccepted: true,
    });
  } catch (error) {
    const status = error instanceof ApiError ? error.status : 0;
    const refusedOutright = status >= 400 && status < 500;
    if (refusedOutright) throw error;

    const uncertain = new ApiError(
      'we lost contact while creating your family, so it may already exist',
      { status, path: SIGNUP_PATH, body: error?.body ?? null },
    );
    uncertain.householdMayExist = true;
    uncertain.cause = error;
    throw uncertain;
  }

  const householdError = (message) => {
    const error = new ApiError(message, { status: 0, path: SESSION_PATH, body: null });
    error.householdCreated = true;
    error.tenant = created?.tenant ?? null;
    return error;
  };

  let actor;
  try {
    actor = await fetchAccountSession();
  } catch {
    throw householdError(
      'your family was created, but you were not signed in — please sign in to continue',
    );
  }
  if (!actor) {
    throw householdError(
      'your family was created, but you were not signed in — please sign in to continue',
    );
  }

  const createdTenantId = created?.tenant?.id;
  if (createdTenantId && actor.tenantId !== createdTenantId) {
    throw householdError(
      'your family was created, but this browser is signed in as a different family — please sign in again',
    );
  }

  return {
    actor,
    tenant: created?.tenant ?? null,
    verificationEmailSent: !!created?.verificationEmailSent,
    // #638. A Google signup arrives verified and is sent no email, so `verificationEmailSent` is
    // absent there. Without this a caller cannot tell "no email was needed" from "it failed".
    emailVerified: created?.user?.emailVerified === true,
  };
}

/**
 * Spend a verification token from the email.
 *
 * `GET /api/auth/signup/verify-email?token=…` — `signup.routes.js:160`. It is NOT guarded by
 * `verifyToken`, and that is correct: the family cannot sign in until this succeeds, so requiring
 * a session would be a locked door with the key inside.
 *
 * The token is single use and expires 24 hours after signup
 * (`signup.service.js` sets `verificationExpires`). Both of those come back as a 4xx the caller
 * turns into a sentence, because "expired" and "already used" need different answers.
 *
 * @param {string} token
 * @returns {Promise<object>} the server's payload.
 * @throws {ApiError}
 */
export async function verifyEmail(token) {
  const value = typeof token === 'string' ? token.trim() : '';
  if (!value) {
    throw new ApiError('that verification link is missing its token', {
      status: 0,
      path: VERIFY_EMAIL_PATH,
      body: null,
    });
  }
  return api.get(`${VERIFY_EMAIL_PATH}?token=${encodeURIComponent(value)}`);
}

/**
 * Ask the server for a fresh verification email.
 *
 * `POST /api/auth/signup/resend-verification` — `signup.routes.js:173`. Rate limited to 10 per
 * 15 minutes (`server/index.js:140`), so the caller must not retry it automatically.
 *
 * **A success here does NOT prove delivery.** It proves the server accepted the request.
 *
 * @param {string} email
 * @returns {Promise<object>}
 */
export async function resendVerification(email) {
  const value = typeof email === 'string' ? email.trim() : '';
  if (!value) {
    throw new ApiError('an email address is needed to send a new link', {
      status: 0,
      path: RESEND_VERIFICATION_PATH,
      body: null,
    });
  }
  await ensureCsrfToken();
  return api.post(RESEND_VERIFICATION_PATH, { email: value });
}

/**
 * Ask the server which family is signed in.
 *
 * `GET /api/tenants/current` answers `{ success, tenant }` — `signup.routes.js:269`. It is guarded
 * by `verifyToken`, so a missing session is a 401 and the CALLER decides what that means, exactly
 * as `fetchSession` does.
 *
 * @returns {Promise<object|null>} the tenant, or `null` when the payload names none.
 * @throws {ApiError} for 401 and for anything that is not a clean answer.
 */
export async function fetchTenant() {
  const payload = await api.get(TENANT_PATH);
  const tenant = payload && typeof payload === 'object' ? payload.tenant : null;
  if (!tenant || typeof tenant !== 'object' || Array.isArray(tenant)) return null;
  return tenant;
}

/**
 * Ask the server who is in this family.
 *
 * `GET /api/users` answers a bare ARRAY, not an envelope — `user.routes.js:18` calls
 * `res.json(users)` directly. That is why this returns `[]` rather than reading a `.users` key.
 *
 * Each row carries `id`, `username`, `first_name`, `display_name`, `avatar_url`, `avatar_type`,
 * `role` and a server-computed `age`. **It does NOT carry `birth_date`**, deliberately: the wizard
 * needs "6", not a date of birth, and no endpoint in this product exposes one.
 *
 * Guarded by `requireAuth`, so a missing session is a 401 and the CALLER decides what that means.
 *
 * @returns {Promise<Array<object>>} the family's members, or `[]` when the answer is not a list.
 * @throws {ApiError} for 401 and for anything that is not a clean answer.
 */
export async function fetchFamilyMembers() {
  const payload = await api.get(MEMBERS_PATH);
  return Array.isArray(payload) ? payload : [];
}

/**
 * Save the family settings the onboarding wizard collects (F-30).
 *
 * `PATCH /api/tenants/current` — guarded by `verifyToken` + `verifyAdmin`, so only a parent may
 * change the family payday. Any subset of the settings may be sent, and at least one must be.
 *
 * **The returned tenant is the SERVER's answer, not the browser's hope.** A caller must publish
 * what comes back rather than the object it sent, so a value the server clamped or refused is
 * never shown as saved.
 *
 * `onboardingCompleted: true` marks when a family finished setting up. The server preserves the
 * original timestamp on every later save, so sending it again is safe and does not rewrite it.
 *
 * @param {{paydayPeriod?: string, approvalRequired?: boolean, weekendGrace?: boolean,
 *          familyActivityEnabled?: boolean, onboardingCompleted?: true}} patch
 * @returns {Promise<object>} the family, in the same shape `fetchTenant` returns.
 * @throws {ApiError} 400 for a body the schema refuses, 401 with no session, 403 for a
 *   non-admin member, 404 when the session names a family that is gone.
 */
export async function updateTenant(patch) {
  const body = patch && typeof patch === 'object' && !Array.isArray(patch) ? patch : {};
  // Fail before the network. An empty PATCH is a 400 the family can do nothing about, and sending
  // it would make a caller believe a save was attempted.
  if (Object.keys(body).length === 0) {
    throw new ApiError('name at least one setting to change', {
      status: 0,
      path: TENANT_PATH,
      body: null,
    });
  }

  await ensureCsrfToken();
  const payload = await api.patch(TENANT_PATH, body);
  const tenant = payload && typeof payload === 'object' ? payload.tenant : null;
  if (!tenant || typeof tenant !== 'object' || Array.isArray(tenant)) {
    // A 200 that does not describe the family is a fault, not a save. Returning the patch here
    // would let a screen celebrate a write nobody can prove happened.
    throw new ApiError('the settings were sent, but the server did not say what it saved', {
      status: 200,
      path: TENANT_PATH,
      body: payload,
    });
  }
  return tenant;
}

/**
 * Sign out. The server destroys its own session and clears the cookies.
 *
 * A 401 is treated as success (review L2): `/api/auth/logout` is guarded by `verifyToken`, so
 * asking to sign out when already signed out answers 401. That is the state the caller wanted.
 *
 * @returns {Promise<null>} always `null`, so a caller can write `setActor(await signOut())`.
 */
export async function signOut() {
  try {
    await api.post(LOGOUT_PATH, {});
  } catch (error) {
    if (!(error instanceof ApiError) || error.status !== 401) throw error;
  }
  return null;
}

/**
 * Ask the server which family profiles this device may show (#576 L17, E29).
 *
 * `server/index.js:202` mounts this router and `profile.routes.js:83` answers
 * `{ profiles: [...] }`. Each row is deliberately NARROW — `profile.service.js:164` selects
 * `id, first_name, avatar_url, role, pin_hash IS NOT NULL AS pin_set` and nothing else. The hash
 * never leaves the database, and `email`, `birth_date`, `last_login`, `pin_failed_attempts` and
 * `pin_locked_until` are absent on purpose: a kitchen display renders this list.
 *
 * **A 401 is an ANSWER here, not a fault, and the caller must treat it that way.**
 * `profile.routes.js:63` `requireAccount` refuses a browser that carries neither a session nor a
 * display token, which is precisely the unknown device `Login.jsx:7-8` describes. So this throws
 * and `LoginRoute` reads the throw as "nobody is remembered on this device".
 *
 * `pinLength` is NOT mapped, because no endpoint publishes one. Inventing a 4 here would put a
 * number on screen that the server never said, and the PIN pad carries its own default.
 *
 * #580 D1 (#898): beside `profiles`, `taps` says what a tap on each tile would do, from the rule the
 * activation itself uses (`ordinary`, `turn`, or `refused` with the server's words). An older server
 * sends none, and every tile then behaves as before. See `tileFor`.
 *
 * @returns {Promise<Array<object>>} picker rows in `Login.jsx`'s shape; `[]` when the payload
 *   names none.
 * @throws {ApiError} for 401 (no account on this device) and for any transport fault.
 */
export async function fetchProfiles() {
  const payload = await api.get(PROFILES_PATH);
  const rows = payload && typeof payload === 'object' ? payload.profiles : null;
  if (!Array.isArray(rows)) return [];
  const taps = payload.taps && typeof payload.taps === 'object' ? payload.taps : {};
  // The server's keys, exactly: `server/services/profile.service.js` `listProfiles` sends
  // `{ userId, firstName, avatarUrl, role, pinSet }`. This read `id`/`first_name`/`pin_set` until
  // #578, which dropped every row and left the picker empty — measured live on dev 2026-09-22.
  return rows
    .filter((row) => row && typeof row === 'object' && row.userId != null)
    .map((row) => ({
      id: row.userId,
      name: row.firstName,
      avatar: row.avatarUrl || null,
      ...tileFor(row, taps[row.userId]),
    }));
}

/** `profile.routes.js` `activationPath`'s words when a turn needs a PIN that is not set. */
const PIN_FIRST = 'Ask a parent to set your PIN first';

/**
 * How one tile behaves, from what the server says a tap on it would do (#898, `taps`).
 *
 * A grown-up always proves who they are with the password form beside the picker, even when a PIN
 * is set: the owner's rule (#578) is that a PIN can never stand in for a parent. Otherwise, with no
 * answer or an `ordinary` one, today's rule: a child with a PIN gets the pad; a child without one is
 * 'open' ("tap and you are in"). A tap that starts a FAMILY TURN gets the pad and says so before the
 * first digit (`startsTurn`); the turn always needs a PIN, so without one the tile explains instead.
 * A tap the server would refuse never taps in, and shows the server's own words.
 */
function tileFor(row, tap) {
  if (row.role === 'admin') return { authMethod: 'password' };
  if (tap?.path === 'refused') {
    return { authMethod: 'none', notice: typeof tap.message === 'string' && tap.message ? tap.message : 'Ask a parent for help with this one.' };
  }
  if (tap?.path === 'turn') return row.pinSet === true ? { authMethod: 'pin', startsTurn: true } : { authMethod: 'none', notice: PIN_FIRST };
  return { authMethod: row.pinSet === true ? 'pin' : 'open' };
}

/**
 * Become the acting profile on this device (E30, #578 L17/L21). The SERVER checks the PIN.
 *
 * The three answers a family can act on are returned as data, because none of them is a fault:
 * `ok`, `rejected` (every wrong, missing or malformed PIN answers the same 401 by design), and
 * `locked` (429, with the server's wait). Anything else is thrown, so the screen can say it could
 * not check rather than blaming the child's PIN.
 *
 * @param {number|string} userId
 * @param {string} [pin] omitted for a member with no PIN set
 * @returns {Promise<{outcome:'ok',actingProfile:object,familyTurn?:object}|{outcome:'rejected'}|{outcome:'locked',retryAfterSeconds:number}>}
 */
export async function activateProfile(userId, pin) {
  await ensureCsrfToken();
  const path = `${PROFILES_PATH}/${encodeURIComponent(userId)}/activate`;
  let payload;
  const requestedAt = childClockStamp();
  try {
    payload = await api.post(path, pin === undefined ? {} : { pin });
  } catch (error) {
    // Only the TAGGED refusal is a wrong PIN. An untagged 401 is an expired session, and blaming
    // the child's PIN for it would teach them their right PIN is wrong.
    if (error instanceof ApiError && error.status === 401 && error.body?.code === 'PIN_REJECTED') {
      return { outcome: 'rejected' };
    }
    if (error instanceof ApiError && error.status === 429) {
      const wait = Number(error.body?.retryAfterSeconds);
      return { outcome: 'locked', retryAfterSeconds: Number.isFinite(wait) ? wait : null };
    }
    throw error;
  }
  // A success must name the child it was asked for. Anything else is not a confirmation, and the
  // screen must never show a child the server did not name.
  const acting = payload?.actingProfile;
  if (!acting || typeof acting !== 'object' || String(acting.userId) !== String(userId)) {
    throw new ApiError('the server confirmed a profile without naming it', { status: 200, path, body: payload });
  }
  childRequestTimes.set(acting, requestedAt);
  // #580 D1: a family turn's activation also describes the turn. It is kept, read like boot's.
  if (payload.familyTurn === undefined) return { outcome: 'ok', actingProfile: acting };
  return { outcome: 'ok', actingProfile: acting, familyTurn: toFamilyTurn(payload.familyTurn) };
}

/**
 * The actor a screen holds while a child is acting on a family device (#578 L17/L21).
 *
 * It names the child and carries NO authority: no `role`, no `isAdmin`. The server keeps the
 * account and the parent window; this object only says whose name to show. `account` is the
 * signed-in account when the caller has one, so the household fields stay filled.
 *
 * #582: `lifetimeMs` is expiresAt - serverNow, anchored to the request's SEND on both
 * local clocks. The compatibility field `receivedAt` holds that send stamp. This charges
 * the entire round trip, preferring an early return to one extended by a slow answer.
 * Request stamps are local WeakMap metadata, never server fields. Without serverNow, an
 * older server's absolute expiry uses the wall clock at send. A direct fixture with no
 * request stamp starts at conversion; production PIN/read/boot paths always supply one.
 *
 * @param {{userId:number|string, firstName:string|null, expiresAt?:string, serverNow?:string}} actingProfile
 * @param {object|null} [account]
 * @param {{mono:number,wall:number}} [requestedAt] send stamp for an injected renewal reader
 * @returns {object|null}
 */
export function toChildActor(actingProfile, account = null, requestedAt) {
  if (!actingProfile || actingProfile.userId == null) return null;
  const stamp = childRequestTimes.get(actingProfile) ?? requestedAt ?? childClockStamp();
  return {
    id: String(actingProfile.userId),
    kind: 'child',
    name: actingProfile.firstName ?? '',
    firstName: actingProfile.firstName ?? '',
    expiresAt: actingProfile.expiresAt ?? null,
    lifetimeMs: childLifetimeMs(actingProfile, stamp),
    receivedAt: stamp,
    tenantId: account?.tenantId ?? null,
    tenantSlug: account?.tenantSlug ?? null,
    // #580 D1: a family turn's member carries its turn generation, which a write must echo.
    ...(typeof actingProfile.generation === 'string' && actingProfile.generation ? { generation: actingProfile.generation } : {}),
  };
}

/** Elapsed time that a wall-clock change cannot move (the operator console's `monotonicNow`). */
export const monotonicNow = () => performance.now();

const childRequestTimes = new WeakMap();
const childClockStamp = () => ({ mono: monotonicNow(), wall: Date.now() });

function childLifetimeMs({ expiresAt, serverNow }, stamp) {
  const end = typeof expiresAt === 'string' ? Date.parse(expiresAt) : NaN;
  if (!Number.isFinite(end)) return -Infinity;
  const now = typeof serverNow === 'string' ? Date.parse(serverNow) : NaN;
  return end - (Number.isFinite(now) ? now : stamp.wall);
}

/** E31 — nobody is acting any more. Idempotent on the server. */
export async function deactivateProfile() {
  await ensureCsrfToken();
  return api.post(`${PROFILES_PATH}/deactivate`, {});
}

/**
 * Who is acting on this device right now, or null (#578 gap G2). The server reads an expired
 * actor as nobody, and each read slides the expiry.
 */
export async function fetchActingProfile() {
  const requestedAt = childClockStamp();
  const payload = await api.get(ACTOR_PATH);
  const acting = payload?.actingProfile ?? null;
  if (acting && typeof acting === 'object') childRequestTimes.set(acting, requestedAt);
  return acting;
}

/**
 * Is the parent window open, and which re-check may the prompt offer? (`GET /api/auth/reauth`)
 *
 * @returns {Promise<{fresh:boolean, freshUntil:string|null, methods:{password:boolean, google:boolean}}>}
 */
export async function fetchReauthStatus() {
  return api.get(REAUTH_PATH);
}

/**
 * The parent re-check (#578 AD-05): prove it is the parent, and open the 5-minute window.
 *
 * @param {string} password
 * @returns {Promise<{outcome:'ok',until:string}|{outcome:'rejected'}|{outcome:'use-google'}>}
 */
export async function reauthWithPassword(password) {
  await ensureCsrfToken();
  try {
    const payload = await api.post(REAUTH_PATH, { password });
    return { outcome: 'ok', until: payload?.parentProofUntil ?? null };
  } catch (error) {
    // The server tags both answers (`auth.routes.js`). An untagged 401 is an expired session.
    if (error instanceof ApiError && error.status === 401 && error.body?.type === 'Parent check rejected') {
      return { outcome: 'rejected' };
    }
    if (error instanceof ApiError && error.status === 400 && error.body?.type === 'Google SSO account') {
      return { outcome: 'use-google' };
    }
    throw error;
  }
}

/**
 * Ask the server to send a password-reset link (#576 L26).
 *
 * `server/auth/routes/auth.routes.js:216` answers 200 whether or not the address belongs to an
 * account, and that is the design, not an oversight: a different answer for a known address turns
 * this into an account-existence oracle. The caller must therefore show the SAME calm line either
 * way, and `app/src/pages/Auth.jsx` already writes it that way.
 *
 * @param {string} email
 * @returns {Promise<object>} the server's payload; its shape carries no account signal
 * @throws {ApiError} for a transport fault only
 */
export async function requestPasswordReset(email) {
  await ensureCsrfToken();
  return api.post(FORGOT_PASSWORD_PATH, { email });
}

/**
 * Check a reset token BEFORE showing the new-password fields (#576 L26).
 *
 * `auth.routes.js:228` answers 200 with the service's verdict. Asking first is what lets the
 * screen say "this link has expired" instead of collecting a password and then refusing it.
 *
 * @param {string} token
 * @returns {Promise<object>} the server's verdict
 */
export async function verifyResetToken(token) {
  return api.get(`${VERIFY_RESET_TOKEN_PATH}/${encodeURIComponent(token)}`);
}

/**
 * Spend a reset token on a new password (#576 L26).
 *
 * The body field is `newPassword`, not `password` — `auth.schemas.js:47-50`. Getting that wrong
 * is a 400 that reads like a rejected password.
 *
 * @param {string} token
 * @param {string} newPassword
 */
export async function resetPassword(token, newPassword) {
  await ensureCsrfToken();
  return api.post(RESET_PASSWORD_PATH, { token, newPassword });
}

/**
 * Read an invitation without accepting it (#576 L26).
 *
 * The endpoint always answers 200. An unknown, expired or used token comes back as
 * `{valid: false, reason}` and names NOBODY — no family, no inviter, no email — so a dead link
 * forwarded to a stranger cannot tell them who lives there. Treat the reply as data, never as an
 * error to alarm somebody with.
 *
 * @param {string} token
 * @returns {Promise<object>} `{valid, familyName, inviterName, role, email, name, expiresAt}`
 *   or `{valid: false, reason}`
 */
export async function fetchInvitationPreview(token) {
  return api.get(`${INVITATIONS_PATH}/${encodeURIComponent(token)}/preview`);
}

/**
 * Accept an invitation and arrive inside the family (#576 L26).
 *
 * `server/auth/routes/signup.routes.js` creates the session on success, exactly as login and
 * signup do, so the caller does NOT then sign in. It asks who is acting, for the same measured
 * reason `signUp` does: a 2xx proves the account exists and nothing about the session.
 *
 * `tosAccepted` must be literally `true` — `signup.schemas.js:57` uses `z.literal(true)`, so a
 * truthy value is rejected.
 *
 * @param {string} token
 * @param {{username: string, password: string, firstName: string, lastName: string,
 *          tosAccepted: boolean}} form
 * @returns {Promise<object>} the acting person
 * @throws {ApiError} when the account exists but the session did not stick
 */
export async function acceptInvitation(token, form) {
  await ensureCsrfToken();
  await api.post(`${INVITATIONS_PATH}/${encodeURIComponent(token)}/accept`, form);

  const actor = await fetchAccountSession();
  if (!actor) {
    throw new ApiError(
      'your account was created, but the sign-in did not stick — please sign in to continue',
      { status: 0, path: SESSION_PATH, body: null },
    );
  }
  return actor;
}

/**
 * The quick actor on a SHARED DISPLAY (#582 S3). A different record from the account's acting
 * profile above, with its own clock: 30 seconds from the last deliberate interaction, never renewed
 * by a read. Every field here is the SERVER's (`server/auth/deviceActor.js` `describeDeviceActor`).
 *
 * @typedef {{userId:number, firstName:string|null, generation:string, expiresAt:string, serverNow:string}} DeviceActor
 */
export const ACTOR_INTERACTION_PATH = '/api/profiles/actor/interaction';

function toDeviceActor(value, expectedUserId) {
  if (!value || typeof value !== 'object') return null;
  if (typeof value.generation !== 'string' || value.generation === '') return null;
  if (!Number.isFinite(Date.parse(value.expiresAt)) || !Number.isFinite(Date.parse(value.serverNow))) return null;
  if (expectedUserId !== undefined && String(value.userId) !== String(expectedUserId)) return null;
  return value;
}

const ACTOR_ENDED_CODES = new Set(['ACTOR_REQUIRED', 'ACTOR_INVALID']);

/** Is this refusal "the actor is gone" (expired, switched, or its member no longer qualifies)? */
export function isActorEnded(error) {
  return error instanceof ApiError && error.status === 403 && ACTOR_ENDED_CODES.has(error.body?.code);
}

/**
 * Prove who is at the display with their PIN. The SERVER checks the PIN, clears any previous
 * actor first, and answers with the new generation and deadline.
 *
 * @returns {Promise<{outcome:'ok', actor:DeviceActor, canMoveAnyChore:boolean}|{outcome:'rejected'}|{outcome:'locked', retryAfterSeconds:number|null}|{outcome:'conflict'}>}
 */
export async function activateDeviceActor(userId, pin) {
  const path = `${PROFILES_PATH}/${encodeURIComponent(userId)}/activate`;
  let payload;
  try {
    payload = await api.post(path, { pin }, { scope: 'device' });
  } catch (error) {
    if (error instanceof ApiError && error.status === 401 && error.body?.code === 'PIN_REJECTED') {
      return { outcome: 'rejected' };
    }
    if (error instanceof ApiError && error.status === 429) {
      const wait = Number(error.body?.retryAfterSeconds);
      return { outcome: 'locked', retryAfterSeconds: Number.isFinite(wait) ? wait : null };
    }
    if (error instanceof ApiError && error.status === 409 && error.body?.code === 'ACTOR_CONFLICT') {
      return { outcome: 'conflict' };
    }
    throw error;
  }
  const actor = toDeviceActor(payload?.actingProfile, userId);
  if (!actor) {
    throw new ApiError('the server confirmed a display actor without naming it', { status: 200, path, body: payload });
  }
  // #583: which moves the board may OFFER this person. Only a real `true` widens them; the server
  // still decides each move on its own.
  return { outcome: 'ok', actor, canMoveAnyChore: payload.canMoveAnyChore === true };
}

/** Who is acting on this display right now, or null. A READ: it never renews the actor. */
export async function fetchDeviceActor() {
  const payload = await api.get(ACTOR_PATH, { scope: 'device' });
  return toDeviceActor(payload?.actingProfile);
}

/**
 * Report ONE deliberate interaction. Call it only through `lib/actorInteraction.js`, which keeps
 * the no-timer, deduplicated, one-per-second rules.
 *
 * @returns {Promise<{outcome:'renewed'|'unchanged', actor:DeviceActor}|{outcome:'ended'}>}
 */
export async function reportDeviceActorInteraction({ generation, interactionId, kind }) {
  let payload;
  try {
    payload = await api.post(ACTOR_INTERACTION_PATH, { generation, interactionId, kind }, { scope: 'device' });
  } catch (error) {
    if (isActorEnded(error)) return { outcome: 'ended' };
    throw error;
  }
  const actor = toDeviceActor(payload?.actingProfile);
  if (!actor) return { outcome: 'ended' };
  return { outcome: payload.renewed === true ? 'renewed' : 'unchanged', actor };
}

/** Nobody is acting on this display any more. Idempotent on the server. */
export async function deactivateDeviceActor({ keepalive = false } = {}) {
  return api.post(`${PROFILES_PATH}/deactivate`, {}, { scope: 'device', keepalive });
}
